/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Button, Divider, Input, Typography, message, Modal } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import FileProgressContext from '~src/contexts/FileProgressContext';

import IconButton from '~components/_common/IconButton/IconButton';
import { PaperClipOutlined } from '@ant-design/icons';
import { SimpleConfirmModal } from '~components/popups-modals/ConfirmModal/SimpleConfirmModal';
import { queryCache, useMutation } from 'react-query';

import {
  deleteCurrentCorrectionLetterAttachment,
  getCorrectionLetterAttachmentDownloadUrl,
  getCorrectionLetterAttachmentDownloadUrlVendor,
} from '~src/services/api/movements';
import Viewer from '~components/atoms/Viewers/Viewer';

type CorrectionLetter = {
  id: number;
  title: string;
  description: string;
  status: number;
  movement_ids?: number[];
  comment_ids?: number[];
  question_ids?: number[];
  modification_report?: { file_guid: string; name: string } | null;
  attachments?: { file_guid: string; name: string }[];
  serial_number?: string;
};

export type LetterTabProps = {
  loading: boolean;
  error: boolean;
  isBusy: boolean;
  projectId: number;
  tenderId: number;
  letter?: CorrectionLetter;

  /**
   * Parent can track "unsaved changes" state and block close / navigation.
   */
  onDirtyChange?: (isDirty: boolean) => void;

  /**
   * When parent decides to discard local changes (e.g. user confirmed
   * Discard & switch tab), it can bump this value to force reset.
   */
  resetNonce?: number;

  onSaveTitleDescription: (payload: {
    title: string;
    description: string;
  }) => void;
  onOpenFileByGuid?: (fileGuid: string, fileName?: string) => void;
  onRefetchLetter?: () => void;
  isInQaTab?: boolean;
  readOnly?: boolean;
  shareId?: string;
  shareKey?: string;
};

export const LetterTab = forwardRef<any, LetterTabProps>(
  (
    {
      loading,
      error,
      isBusy,
      projectId,
      tenderId,
      letter,
      onDirtyChange,
      resetNonce,
      onSaveTitleDescription,
      onOpenFileByGuid,
      onRefetchLetter,
      isInQaTab,
      readOnly,
      shareId,
      shareKey,
    },
    ref
  ) => {
    const intl = useIntl();
    const styles = useStyles(styleFn);

    const { setFileProgressContext, fileProgressContext } = useContext(
      FileProgressContext
    );

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const MAX_ATTACHMENT_SIZE_BYTES = 4 * 1024 * 1024; // 4 MB
    const MAX_ATTACHMENTS = 5;

    // ==hooks (local state)
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');

    useImperativeHandle(ref, () => ({
      save: () => {
        onSaveTitleDescription({ title, description });
      },
    }));

    const [
      showConfirmDeleteAttachmentModal,
      setShowConfirmDeleteAttachmentModal,
    ] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState<{
      file_guid: string;
      name: string;
    } | null>(null);

    // Drawer state for file preview
    const [previewDrawerVisible, setPreviewDrawerVisible] = useState(false);
    const [previewFile, setPreviewFile] = useState<{
      url: string;
      name: string;
      extension: string;
    } | null>(null);
    const [isLoadingUrl, setIsLoadingUrl] = useState(false);

    const [
      doDeleteAttachment,
      { isLoading: isDeletingAttachment },
    ] = useMutation(deleteCurrentCorrectionLetterAttachment, {
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'attachment-delete-successful' })
        );

        // refresh current letter so attachment disappears immediately
        onRefetchLetter?.();
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'attachment-delete-failed' }));
      },
    });

    // Keep local inputs in sync when letter loads/refetches
    useEffect(() => {
      if (!letter) return;
      setTitle(letter.title || '');
      setDescription(letter.description || '');
    }, [letter?.id]); // only when letter instance changes (new version)

    // Force-reset local inputs when parent asks to discard local changes
    useEffect(() => {
      if (!letter) return;
      setTitle(letter.title || '');
      setDescription(letter.description || '');
    }, [resetNonce]);

    const hasChanges =
      (letter?.title ?? '') !== title ||
      (letter?.description ?? '') !== description;

    // Report "dirty" state to parent so it can block close / navigation
    useEffect(() => {
      onDirtyChange?.(hasChanges);
    }, [hasChanges, onDirtyChange]);

    const openAttachmentPicker = () => {
      if (isBusy) return;
      fileInputRef.current?.click();
    };

    const onAttachmentSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      if (!picked.length) return;

      // allow picking same file again
      e.target.value = '';

      const existingCount = letter?.attachments?.length ?? 0;
      const remainingSlots = Math.max(0, MAX_ATTACHMENTS - existingCount);

      if (remainingSlots <= 0) {
        message.error(`Maximum ${MAX_ATTACHMENTS} files.`);
        return;
      }

      if (picked.length > remainingSlots) {
        message.warning(`You can upload up to ${MAX_ATTACHMENTS} files.`);
      }

      const filesToUpload = picked.slice(0, remainingSlots);
      const oversize = filesToUpload.find(
        (f) => f.size > MAX_ATTACHMENT_SIZE_BYTES
      );
      if (oversize) {
        message.error(intl.formatMessage({ id: 'upload-max-4mb' }));
        return;
      }

      // push into FileProgressContext - FileProgressItem will route by flag
      setFileProgressContext((prev) => [
        ...prev,
        ...filesToUpload.map(
          (file) =>
            ({
              id: `correlation_letter_attachment_${Date.now()}_${Math.random()
                .toString(16)
                .slice(2)}`,
              file,
              projectId,
              tenderId: String(tenderId),

              // metadata used by tus attachments endpoints (consistent with other uploaders)
              versionStr: ' ',
              description: ' ',

              // upload flow flags
              isDone: false,
              shouldStartUpload: true,

              // IMPORTANT: tells FileProgressItem to use /current_correction_letter/attachments/tus
              isCorrelationLetterAttachment: true,
            } as any)
        ),
      ]);
    };

    // Refresh current letter after its upload finishes (same pattern as TermOfServiceCard)
    useEffect(() => {
      if (!projectId || !tenderId) return;

      const hasPendingUploads = fileProgressContext?.some(
        (f: any) =>
          f?.isCorrelationLetterAttachment &&
          String(f?.tenderId) === String(tenderId) &&
          !f?.isDone
      );

      if (!hasPendingUploads && fileProgressContext?.length > 0) {
        // Call the refetch callback from parent to refresh the query
        onRefetchLetter?.();
      }
    }, [fileProgressContext, projectId, tenderId, onRefetchLetter]);

    // Helper functions for drawer preview
    const isPreviewable = (filename: string): boolean => {
      const ext = filename.split('.').pop()?.toLowerCase() || '';
      const isPdf = /^pdf$/i.test(ext);
      const isImage = /^(jpg|jpeg|png|gif|webp)$/i.test(ext);
      return isPdf || isImage;
    };

    const handleDrawerClose = () => {
      setPreviewDrawerVisible(false);
      setPreviewFile(null);
    };

    const handleFileDownload = () => {
      if (previewFile?.url) {
        const link = document.createElement('a');
        link.href = previewFile.url;
        link.download = previewFile.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    const handleAttachmentPreview = async (
      fileGuid: string,
      filename: string
    ) => {
      if (!letter?.id) return;

      const extension = filename.split('.').pop() || '';
      if (!isPreviewable(filename)) return;

      // Open drawer immediately with empty URL (shows loading spinner)
      setPreviewFile({ url: '', name: filename, extension });
      setPreviewDrawerVisible(true);
      setIsLoadingUrl(true);

      try {
        let url = '';

        if (shareId && shareKey) {
          // Vendor side download
          url = await getCorrectionLetterAttachmentDownloadUrlVendor({
            shareId,
            shareKey,
            tender_correction_letter_id: Number(letter.id),
            file_guid: fileGuid,
          });
        } else {
          // Project side download
          url = await getCorrectionLetterAttachmentDownloadUrl({
            project_id: Number(projectId),
            tender_id: Number(tenderId),
            tender_correction_letter_id: Number(letter.id),
            file_guid: fileGuid,
          });
        }

        if (!url) {
          message.error(
            intl.formatMessage({ id: 'attachment-download-failed' })
          );
          setPreviewDrawerVisible(false);
          setPreviewFile(null);
          return;
        }

        // Update with actual URL - Viewer will re-render with content
        setPreviewFile({ url, name: filename, extension });
      } catch (e) {
        message.error(intl.formatMessage({ id: 'attachment-download-failed' }));
        setPreviewDrawerVisible(false);
        setPreviewFile(null);
      } finally {
        setIsLoadingUrl(false);
      }
    };

    const handleDownloadAttachment = async (file_guid: string) => {
      if (!letter?.id) return;

      try {
        let url = '';

        if (shareId && shareKey) {
          // Vendor side download
          url = await getCorrectionLetterAttachmentDownloadUrlVendor({
            shareId,
            shareKey,
            tender_correction_letter_id: Number(letter.id),
            file_guid,
          });
        } else {
          // Project side download
          url = await getCorrectionLetterAttachmentDownloadUrl({
            project_id: Number(projectId),
            tender_id: Number(tenderId),
            tender_correction_letter_id: Number(letter.id),
            file_guid,
          });
        }

        if (!url) {
          message.error(
            intl.formatMessage({ id: 'attachment-download-failed' })
          );
          return;
        }

        window.open(url, '_blank');
      } catch (e) {
        message.error(intl.formatMessage({ id: 'attachment-download-failed' }));
      }
    };

    const handleConfirmDeleteAttachment = async () => {
      if (!attachmentToDelete) return;

      await doDeleteAttachment({
        project_id: Number(projectId),
        tender_id: Number(tenderId),
        file_guid: attachmentToDelete.file_guid,
      });

      setAttachmentToDelete(null);
      setShowConfirmDeleteAttachmentModal(false);
    };

    const handleReset = () => {
      Modal.confirm({
        title: intl.formatMessage({ id: 'confirm-reset-title' }),
        content: intl.formatMessage({ id: 'confirm-reset-message' }),
        okText: intl.formatMessage({ id: 'reset' }),
        cancelText: intl.formatMessage({ id: 'cancel' }),
        okType: 'danger',
        onOk: () => {
          setTitle(letter?.title || '');
          setDescription(letter?.description || '');
        },
      });
    };

    // ==return
    if (loading) {
      return (
        <div css={styles.stateBox}>
          <Typography.Text type="secondary">
            {intl.formatMessage({ id: 'loading' })}
          </Typography.Text>
        </div>
      );
    }

    if (error || !letter) {
      return (
        <div css={styles.stateBox}>
          <Typography.Text type="danger">
            {intl.formatMessage({ id: 'correction-letter-load-failed' })}
          </Typography.Text>
        </div>
      );
    }

    return (
      <div css={styles.wrapper}>
        {/* == Title / Description */}
        <div css={styles.section}>
          <Typography.Title level={5} css={styles.sectionTitle}>
            {intl.formatMessage({ id: 'correction-letter-details' })}{' '}
            {`<${letter?.serial_number}>`}
          </Typography.Title>

          <div
            css={
              readOnly
                ? [{ display: 'flex', flexDirection: 'column' }]
                : styles.formRow
            }>
            <div css={styles.label}>{intl.formatMessage({ id: 'title' })}</div>
            {readOnly ? (
              <div style={{ padding: '5px 0' }}>
                <Typography.Text>{title || '-'}</Typography.Text>
              </div>
            ) : (
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={isBusy || readOnly}
              />
            )}
          </div>

          <div css={styles.formRowColumn}>
            <div css={styles.label}>
              {intl.formatMessage({ id: 'description' })}
            </div>
            {readOnly ? (
              <div style={{ padding: '5px 0' }}>
                <Typography.Text>{description}</Typography.Text>
              </div>
            ) : (
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                autoSize={{ minRows: 5, maxRows: 10 }}
                disabled={isBusy || readOnly}
              />
            )}
          </div>

          {!readOnly && (
            <div css={styles.actionsRow}>
              <Button disabled={isBusy || !hasChanges} onClick={handleReset}>
                {intl.formatMessage({ id: 'reset' })}
              </Button>

              <div style={{ flex: 1 }} />

              <Button
                type="primary"
                disabled={isBusy || !hasChanges}
                onClick={() => onSaveTitleDescription({ title, description })}>
                {intl.formatMessage({ id: 'save' })}
              </Button>
            </div>
          )}
        </div>

        <Divider css={styles.divider} />

        {/* == Files */}
        <div css={styles.section}>
          <Typography.Title level={5} css={styles.sectionTitle}>
            {intl.formatMessage({ id: 'correction-letter-files-title' })}
          </Typography.Title>

          <div css={styles.filesGrid}>
            <div css={styles.fileBox}>
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}>
                <div css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Typography.Text strong>
                    {intl.formatMessage({
                      id: 'correction-letter-attachments',
                    })}
                  </Typography.Text>
                  <Typography.Text
                    italic
                    type={'secondary'}
                    css={{ fontSize: '12px' }}>
                    ({intl.formatMessage({ id: 'upload-max-4mb' })}, max{' '}
                    {MAX_ATTACHMENTS})
                  </Typography.Text>
                </div>

                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={onAttachmentSelected}
                  />

                  {!readOnly && (
                    <Button
                      onClick={openAttachmentPicker}
                      disabled={
                        isBusy ||
                        (letter.attachments?.length ?? 0) >= MAX_ATTACHMENTS
                      }>
                      {intl.formatMessage({ id: 'upload' })}
                    </Button>
                  )}
                </div>
              </div>

              {letter.attachments?.length ? (
                <div css={styles.attachmentsList}>
                  {letter.attachments.map((a) => {
                    const isPdf =
                      a.name?.toLowerCase()?.endsWith('.pdf') ||
                      a.name?.toLowerCase()?.includes('.pdf');

                    return (
                      <div
                        key={a.file_guid}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                        }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            minWidth: 0,
                            flex: 1,
                          }}>
                          {isPdf ? (
                            <IconButton
                              icon={'file-pdf'}
                              iconStyle={'fad'}
                              color={'red'}
                              backgroundOff
                              customStyle={{ fontSize: '18px' }}
                              containerStyle={{
                                maxHeight: '18px',
                                maxWidth: '18px',
                                minHeight: '18px',
                                minWidth: '18px',
                              }}
                            />
                          ) : (
                            // @ts-ignore
                            <PaperClipOutlined style={{ fontSize: '18px' }} />
                          )}

                          <Button
                            type="link"
                            disabled={!onOpenFileByGuid}
                            onClick={() =>
                              onOpenFileByGuid?.(a.file_guid, a.name)
                            }
                            style={{
                              padding: 0,
                              height: 'auto',
                              textAlign: 'left',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                            }}>
                            {a.name}
                          </Button>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {/* Preview icon for previewable files */}
                          {isPreviewable(a.name) && (
                            <IconButton
                              icon={'eye'}
                              iconStyle={'far'}
                              color={'red'}
                              customStyle={{ fontSize: '16px' }}
                              click={() =>
                                handleAttachmentPreview(a.file_guid, a.name)
                              }
                            />
                          )}

                          <IconButton
                            icon={'download'}
                            iconStyle={'far'}
                            color={'blue'}
                            click={() => handleDownloadAttachment(a.file_guid)}
                            customStyle={{ fontSize: '16px' }}
                          />

                          {!readOnly && (
                            <IconButton
                              icon={'trash'}
                              iconStyle={'far'}
                              color={'red'}
                              click={() => {
                                setAttachmentToDelete(a);
                                setShowConfirmDeleteAttachmentModal(true);
                              }}
                              customStyle={{ fontSize: '16px' }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Typography.Text type="secondary">
                  {intl.formatMessage({
                    id: 'correction-letter-attachments-not-implemented',
                  })}
                </Typography.Text>
              )}
            </div>

            {!isInQaTab && (
              <div css={styles.fileBox}>
                <Typography.Text strong>
                  {intl.formatMessage({ id: 'correction-letter-mod-report' })}
                </Typography.Text>

                {letter.modification_report?.file_guid ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        minWidth: 0,
                        flex: 1,
                      }}>
                      {letter.modification_report.name
                        ?.toLowerCase()
                        ?.includes('.pdf') ? (
                        <IconButton
                          icon={'file-pdf'}
                          iconStyle={'fad'}
                          color={'red'}
                          backgroundOff
                          customStyle={{ fontSize: '18px' }}
                          containerStyle={{
                            maxHeight: '18px',
                            maxWidth: '18px',
                            minHeight: '18px',
                            minWidth: '18px',
                          }}
                        />
                      ) : (
                        // @ts-ignore
                        <PaperClipOutlined style={{ fontSize: '18px' }} />
                      )}

                      <Typography.Text
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                        }}>
                        {letter.modification_report.name}
                      </Typography.Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {/* Preview icon for previewable files */}
                      {isPreviewable(letter.modification_report.name) && (
                        <IconButton
                          icon={'eye'}
                          iconStyle={'far'}
                          color={'red'}
                          customStyle={{ fontSize: '16px' }}
                          click={() =>
                            handleAttachmentPreview(
                              letter.modification_report!.file_guid,
                              letter.modification_report!.name
                            )
                          }
                        />
                      )}

                      <IconButton
                        icon={'download'}
                        iconStyle={'far'}
                        color={'blue'}
                        click={() =>
                          handleDownloadAttachment(
                            letter.modification_report!.file_guid
                          )
                        }
                        customStyle={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>
                ) : (
                  <Typography.Text type="secondary">
                    {intl.formatMessage({
                      id: 'correction-letter-no-mod-report',
                    })}
                  </Typography.Text>
                )}
              </div>
            )}
          </div>
        </div>

        <SimpleConfirmModal
          visible={showConfirmDeleteAttachmentModal}
          isLoading={isDeletingAttachment}
          onOk={handleConfirmDeleteAttachment}
          onCancel={() => {
            setAttachmentToDelete(null);
            setShowConfirmDeleteAttachmentModal(false);
          }}
          modalTitle={`${intl.formatMessage({ id: 'delete-attachment' })} - ${
            attachmentToDelete?.name
          }`}
          modalIcon={['far', 'trash']}
          okText={intl.formatMessage({ id: 'delete' })}
          okType="danger"
        />

        {/* Drawer preview */}
        {previewFile && (
          <Viewer
            mode="drawer"
            drawerVisible={previewDrawerVisible}
            onDrawerClose={handleDrawerClose}
            drawerTitle={previewFile.name}
            drawerWidth={800}
            fileUrl={previewFile.url}
            fileExtension={previewFile.extension}
            showDownloadButton={true}
            onDownload={handleFileDownload}
            isLoadingUrl={isLoadingUrl}
          />
        )}
      </div>
    );
  }
);

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    paddingBottom: 18,
    paddingTop: 5,
  },
  stateBox: {
    padding: 16,
    background: '#fafafa',
    borderRadius: 6,
    border: '1px solid #f0f0f0',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  sectionTitle: {
    margin: 0,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    alignItems: 'center',
    gap: 12,
  },
  formRowColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  label: {
    fontWeight: 500,
  },
  actionsRow: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  },
  divider: {
    margin: '4px 0',
  },
  filesGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  fileBox: {
    border: '1px solid #f0f0f0',
    borderRadius: 6,
    padding: 12,
    background: '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minHeight: 80,
  },
  attachmentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 6,
  },
});
